import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Bookmark,
  CheckCircle2,
  X,
  XCircle,
} from "lucide-react";
import { FormField, SelectField } from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { ProductPricingSummary } from "@/components/ProductPricingSummary";
import { generateId, useApp, type Card } from "@/context/AppContext";
import { buildDeckProduct } from "@/features/deckProductBuilder";
import { validateProductLot } from "@/features/productLotValidation";
import { DECK_SOURCE_PROVIDERS } from "@/features/deckSourceProviders";
import type {
  DeckFormat,
  DeckInventoryMatch,
  DeckIntent,
  MissingDeckCard,
  ProductDeckBuild,
} from "@/types/deckBuilder";
import type { DeckSourceProviderId } from "@/types/deckSource";

export const Route = createFileRoute("/products/deck-import")({
  component: ProductDeckImportPage,
  validateSearch: (search): { templateId?: string } => ({
    templateId:
      typeof search.templateId === "string" ? search.templateId : undefined,
  }),
});

const DECK_INTENT_OPTIONS: DeckIntent[] = [
  "casual-fun",
  "beginner-friendly",
  "pokemon-specific",
  "type-themed",
  "budget-playable",
  "deck-core",
  "upgrade-kit",
  "competitive-inspired",
];

const DECK_FORMAT_OPTIONS: DeckFormat[] = [
  "Casual",
  "No Specific Format",
  "Standard",
  "Expanded",
  "GLC",
  "Pocket",
];

interface DeckImportFormState {
  pastedDeckList: string;
  intent: DeckIntent;
  format: DeckFormat;
  themeKeyword: string;
  provider: DeckSourceProviderId;
  sourceUrl: string;
  sourceName: string;
}

const DEFAULT_FORM: DeckImportFormState = {
  pastedDeckList: "",
  intent: "casual-fun",
  format: "Casual",
  themeKeyword: "",
  provider: "manual",
  sourceUrl: "",
  sourceName: "",
};

function ProductDeckImportPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const {
    cards,
    addProductWithPackaging,
    shippingSupplies,
    deckTemplates,
    addDeckTemplate,
  } = useApp();

  const [form, setForm] = useState<DeckImportFormState>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const loadedTemplateIdRef = useRef<string | null>(null);
  // Force a re-render when the ref changes so the "active" chip styling updates.
  const [loadedTemplateId, setLoadedTemplateId] = useState<string | null>(null);

  const loadTemplate = (templateId: string) => {
    const template = deckTemplates.find((t) => t.id === templateId);
    if (!template) return;
    loadedTemplateIdRef.current = templateId;
    setLoadedTemplateId(templateId);
    setForm({
      pastedDeckList: template.pastedDeckList,
      intent: template.intent,
      format: template.format,
      themeKeyword: template.themeKeyword ?? "",
      provider: "local-template",
      sourceUrl: template.sourceUrl ?? "",
      sourceName: template.sourceName ?? template.name,
    });
  };

  // Auto-load ?templateId=… on mount and whenever the deeplink changes.
  useEffect(() => {
    const templateId = search.templateId;
    if (!templateId) return;
    if (loadedTemplateIdRef.current === templateId) return;
    const template = deckTemplates.find((t) => t.id === templateId);
    if (!template) return;
    loadTemplate(templateId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.templateId, deckTemplates]);

  const handleSaveTemplate = (name: string) => {
    addDeckTemplate({
      id: generateId("DTPL"),
      name,
      pastedDeckList: form.pastedDeckList,
      intent: form.intent,
      format: form.format,
      themeKeyword: form.themeKeyword || undefined,
      provider: form.provider,
      sourceUrl: form.sourceUrl || undefined,
      sourceName: form.sourceName || undefined,
      createdAt: new Date().toISOString(),
    });
    setShowSaveModal(false);
  };

  const built = useMemo(() => {
    if (!form.pastedDeckList.trim()) return null;
    try {
      return buildDeckProduct({
        pastedDeckList: form.pastedDeckList,
        inventoryCards: cards,
        intent: form.intent,
        format: form.format,
        themeKeyword: form.themeKeyword || undefined,
        externalSource:
          form.sourceUrl || form.sourceName
            ? {
                provider: form.provider,
                sourceUrl: form.sourceUrl || undefined,
                sourceName: form.sourceName || undefined,
                importedAt: new Date().toISOString(),
              }
            : undefined,
        supplies: shippingSupplies,
      });
    } catch {
      return null;
    }
  }, [
    form.pastedDeckList,
    form.intent,
    form.format,
    form.themeKeyword,
    form.provider,
    form.sourceUrl,
    form.sourceName,
    cards,
    shippingSupplies,
  ]);

  const inventoryById = useMemo(() => {
    const map = new Map<string, Card>();
    for (const c of cards) map.set(c.id, c);
    return map;
  }, [cards]);

  const validation = useMemo(() => {
    if (!built) return { errors: [], warnings: [] };
    return validateProductLot(built.opportunity, inventoryById);
  }, [built, inventoryById]);

  const handleSave = () => {
    if (!built) return;
    if (validation.errors.length > 0) {
      setValidationError(validation.errors[0]);
      return;
    }
    setSaving(true);
    setValidationError(null);
    const productId = generateId("PROD");
    const opp = built.opportunity;
    addProductWithPackaging({
      id: productId,
      productType: opp.productType,
      name: opp.title,
      quantity: 1,
      cardCount: opp.cardCount,
      cardIds: opp.cardIds,
      sourceLotId: "",
      costBasis: opp.estimatedCostBasis,
      packagingCost: 0,
      targetSalePrice: opp.expectedSalePrice,
      listingTitle: opp.title,
      description: opp.description,
      storageLocation: "",
      status: "Draft",
      createdAt: new Date().toISOString(),
      bundleStrategyId: opp.strategyId,
      deckBuild: opp.deckBuild,
      externalDeckSource:
        form.sourceUrl || form.sourceName
          ? {
              provider: form.provider,
              sourceUrl: form.sourceUrl || undefined,
              sourceName: form.sourceName || undefined,
              importedAt: new Date().toISOString(),
            }
          : undefined,
    });
    setSaving(false);
    navigate({ to: "/products/$id", params: { id: productId } });
  };

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <Link
          to="/products"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Close
        </Link>
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto py-4 pr-2">
        <div className="space-y-1">
          <h1 className="text-xl font-bold text-foreground">
            Import deck list
          </h1>
          <p className="text-sm leading-snug text-muted-foreground">
            Paste a TCGplayer-style Pokémon deck list. The app matches it
            against your unlisted inventory and proposes a Pre-built Deck, Deck
            Core, or Upgrade Kit.
          </p>
        </div>

        {deckTemplates.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Load saved deck
              </label>
              <Link
                to="/tools/deck-templates"
                className="text-xs font-semibold text-secondary hover:underline"
              >
                Manage
              </Link>
            </div>
            <div className="flex flex-wrap gap-2 pb-1">
              {deckTemplates.slice(0, 8).map((t) => {
                const active = loadedTemplateId === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => loadTemplate(t.id)}
                    className={`min-w-[60px] max-w-[200px] shrink-0 truncate rounded-md px-3 py-2 text-xs font-semibold ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-foreground hover:bg-muted/80"
                    }`}
                    title={t.name}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <DeckImportForm state={form} onChange={setForm} />

        {form.pastedDeckList.trim().length > 0 && (
          <button
            type="button"
            onClick={() => setShowSaveModal(true)}
            className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-muted px-3 py-2.5 text-sm font-semibold text-foreground hover:bg-muted/80"
          >
            <Bookmark className="h-3.5 w-3.5 text-secondary" />
            Save list as template
          </button>
        )}

        {built && (
          <div className="space-y-3">
            <div>
              <h2 className="text-base font-bold text-foreground">
                {built.opportunity.title}
              </h2>
              <div className="my-2 flex flex-wrap gap-2">
                <Badge
                  label={built.opportunity.productType}
                  variant="default"
                />
                <Badge
                  label={
                    built.opportunity.recommendedListingType === "Auction"
                      ? "Auction"
                      : "Fixed Price"
                  }
                  variant={
                    built.opportunity.recommendedListingType === "Auction"
                      ? "warning"
                      : "success"
                  }
                />
              </div>
              <p className="text-sm leading-snug text-muted-foreground">
                {built.opportunity.listingTypeReason}
              </p>
            </div>

            <DeckMatchPreview matches={built.matches} />

            {built.opportunity.deckBuild && (
              <DeckCompositionSummary deckBuild={built.opportunity.deckBuild} />
            )}

            {built.opportunity.deckBuild && (
              <MissingCardsList
                missingCards={built.opportunity.deckBuild.missingCards}
              />
            )}

            <ProductPricingSummary
              estimatedMarketValue={built.opportunity.estimatedMarketValue}
              estimatedCostBasis={built.opportunity.estimatedCostBasis}
              expectedSalePrice={built.opportunity.expectedSalePrice}
              expectedProfit={built.opportunity.expectedProfit}
              expectedMarginPct={built.opportunity.expectedMarginPct}
              fulfillment={built.opportunity.estimatedFulfillmentCost}
            />

            {validation.warnings.length > 0 && (
              <div className="space-y-1.5 rounded-md border border-amber-500 bg-amber-500/10 p-2.5">
                {validation.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                    <span className="flex-1 text-xs leading-snug text-amber-700">
                      {w}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {validation.errors.length > 0 && (
              <div className="space-y-1.5 rounded-md border border-destructive bg-destructive/10 p-2.5">
                {validation.errors.map((e, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                    <span className="flex-1 text-xs leading-snug text-destructive">
                      {e}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {validationError && (
              <p className="text-xs text-destructive">{validationError}</p>
            )}

            <PrimaryButton
              label={`Save as ${built.opportunity.productType}`}
              onClick={handleSave}
              loading={saving}
              disabled={validation.errors.length > 0}
            />
          </div>
        )}
      </div>

      <SaveDeckTemplateModal
        open={showSaveModal}
        existingNames={deckTemplates.map((t) => t.name)}
        suggestedName={form.themeKeyword || form.sourceName}
        onSave={handleSaveTemplate}
        onClose={() => setShowSaveModal(false)}
      />
    </section>
  );
}

// ── DeckImportForm ─────────────────────────────────────────────────────────

function DeckImportForm({
  state,
  onChange,
}: {
  state: DeckImportFormState;
  onChange: (next: DeckImportFormState) => void;
}) {
  const supportedProviders = DECK_SOURCE_PROVIDERS.filter(
    (p) => p.supportedNow,
  );
  const providerInfo = DECK_SOURCE_PROVIDERS.find(
    (p) => p.id === state.provider,
  );

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <SelectField
          label="Source provider"
          value={state.provider}
          options={supportedProviders.map((p) => p.id)}
          optionLabels={supportedProviders.map((p) => p.displayName)}
          onSelect={(value) =>
            onChange({ ...state, provider: value as DeckSourceProviderId })
          }
        />
        {providerInfo && (
          <p className="text-xs leading-snug text-muted-foreground">
            {providerInfo.description}
          </p>
        )}
      </div>

      <FormField
        label="Source URL (optional)"
        value={state.sourceUrl}
        onChangeText={(text) => onChange({ ...state, sourceUrl: text })}
        placeholder="https://www.tcgplayer.com/article/..."
      />

      <FormField
        label="Source name (optional)"
        value={state.sourceName}
        onChangeText={(text) => onChange({ ...state, sourceName: text })}
        placeholder="e.g. Charizard ex Variants"
      />

      <SelectField
        label="Deck intent"
        value={state.intent}
        options={DECK_INTENT_OPTIONS}
        onSelect={(value) =>
          onChange({ ...state, intent: value as DeckIntent })
        }
      />

      <div className="space-y-1">
        <SelectField
          label="Deck format"
          value={state.format}
          options={DECK_FORMAT_OPTIONS}
          onSelect={(value) =>
            onChange({ ...state, format: value as DeckFormat })
          }
        />
        {state.format !== "Casual" && state.format !== "No Specific Format" && (
          <p className="text-xs font-medium text-amber-600">
            MVP does not verify {state.format} legality. The product description
            will not claim tournament legality.
          </p>
        )}
      </div>

      <FormField
        label="Theme keyword (optional)"
        value={state.themeKeyword}
        onChangeText={(text) => onChange({ ...state, themeKeyword: text })}
        placeholder="e.g. Charizard, Fire, Mew"
      />

      <div className="space-y-1.5">
        <label className="text-xs font-semibold text-foreground">
          Deck list
        </label>
        <textarea
          value={state.pastedDeckList}
          onChange={(e) =>
            onChange({ ...state, pastedDeckList: e.target.value })
          }
          placeholder={`Pokémon (18)\n4 Charmander\n3 Charmeleon\n2 Charizard ex\n\nTrainer (28)\n4 Ultra Ball\n4 Rare Candy\n\nEnergy (14)\n14 Fire Energy`}
          rows={10}
          className="w-full resize-y rounded-md border border-border bg-muted px-3 py-2 font-mono text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        />
      </div>
    </div>
  );
}

// ── DeckMatchPreview ───────────────────────────────────────────────────────

function DeckMatchPreview({ matches }: { matches: DeckInventoryMatch[] }) {
  if (matches.length === 0) {
    return (
      <div className="rounded-md bg-muted p-4 text-center">
        <p className="text-sm text-muted-foreground">
          Paste a deck list above to see how it matches your inventory.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="mb-1.5 text-sm font-bold text-foreground">
        Match preview
      </div>
      {matches.map((m, i) => {
        const fullyMatched = m.missingQty === 0;
        const partiallyMatched = m.ownedQty > 0 && m.missingQty > 0;
        return (
          <div
            key={`${m.requested.raw}-${i}`}
            className="flex items-center justify-between gap-2 border-b border-border py-2 last:border-b-0"
          >
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              <span className="min-w-[42px] text-sm font-bold text-foreground">
                {m.ownedQty}/{m.requestedQty}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-foreground">
                  {m.requested.name}
                  {m.requested.setCode && m.requested.cardNumber
                    ? ` (${m.requested.setCode} ${m.requested.cardNumber})`
                    : ""}
                </div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {m.matchReason}
                </div>
              </div>
            </div>
            {fullyMatched ? (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
            ) : partiallyMatched ? (
              <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
            ) : (
              <XCircle className="h-4 w-4 shrink-0 text-destructive" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── DeckCompositionSummary ─────────────────────────────────────────────────

function DeckCompositionSummary({
  deckBuild,
}: {
  deckBuild: ProductDeckBuild;
}) {
  const counts = { Pokemon: 0, Trainer: 0, Energy: 0, Unknown: 0 };
  for (const line of deckBuild.deckList) counts[line.role] += line.includedQty;
  const total =
    counts.Pokemon + counts.Trainer + counts.Energy + counts.Unknown;
  const targetTotal = 60;
  const fraction = Math.min(1, total / targetTotal);

  return (
    <div className="space-y-2.5 rounded-md border border-border bg-card p-3.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-foreground">
          Deck composition
        </span>
        <span className="text-xs font-medium text-muted-foreground">
          {total} / {targetTotal} cards
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-secondary transition-[width]"
          style={{ width: `${fraction * 100}%` }}
        />
      </div>
      <div className="flex gap-3">
        <Stat label="Pokémon" value={counts.Pokemon} />
        <Stat label="Trainer" value={counts.Trainer} />
        <Stat label="Energy" value={counts.Energy} />
      </div>
      <ScoreRow
        label="Completeness"
        value={deckBuild.deckScore.completenessScore}
      />
      <ScoreRow
        label="Playability"
        value={deckBuild.deckScore.playabilityScore}
      />
      <ScoreRow label="Theme" value={deckBuild.deckScore.themeScore} />
      <ScoreRow
        label="Inventory fit"
        value={deckBuild.deckScore.inventoryFitScore}
      />
      <ScoreRow label="Value" value={deckBuild.deckScore.valueScore} />
      <div className="h-px bg-border" />
      <ScoreRow
        label="Final score"
        value={deckBuild.deckScore.finalScore}
        bold
      />
      {deckBuild.deckScore.warnings.length > 0 && (
        <div className="space-y-1 pt-1">
          {deckBuild.deckScore.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
              <span className="flex-1 text-[11px] leading-tight text-amber-700">
                {w}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-1 flex-col items-center gap-0.5">
      <div className="text-base font-bold text-foreground">{value}</div>
      <div className="text-[11px] font-medium text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function ScoreRow({
  label,
  value,
  bold,
}: {
  label: string;
  value: number;
  bold?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span
        className={`text-xs ${bold ? "font-bold" : "font-medium"} text-foreground`}
      >
        {value}/100
      </span>
    </div>
  );
}

// ── MissingCardsList ───────────────────────────────────────────────────────

function MissingCardsList({
  missingCards,
}: {
  missingCards: MissingDeckCard[];
}) {
  if (missingCards.length === 0) return null;
  const byPriority: Record<MissingDeckCard["priority"], MissingDeckCard[]> = {
    Required: [],
    Recommended: [],
    Optional: [],
  };
  for (const m of missingCards) byPriority[m.priority].push(m);

  return (
    <div className="space-y-2.5 rounded-md border border-border bg-card p-3.5">
      <div className="text-sm font-bold text-foreground">Missing cards</div>
      {(["Required", "Recommended", "Optional"] as const).map((priority) => {
        const group = byPriority[priority];
        if (group.length === 0) return null;
        const toneClass =
          priority === "Required"
            ? "text-destructive"
            : priority === "Recommended"
              ? "text-amber-600"
              : "text-muted-foreground";
        return (
          <div key={priority} className="space-y-1">
            <div
              className={`text-[10px] font-semibold uppercase tracking-wider ${toneClass}`}
            >
              {priority} ({group.length})
            </div>
            {group.map((m, i) => (
              <div
                key={`${m.name}-${i}`}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="truncate text-foreground">
                  {m.name}
                  <span className="ml-1.5 text-[10px] text-muted-foreground">
                    · {m.role}
                  </span>
                </span>
                <span className="shrink-0 font-bold text-foreground">
                  ×{m.missingQty}
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── SaveDeckTemplateModal ──────────────────────────────────────────────────

function SaveDeckTemplateModal({
  open,
  existingNames,
  suggestedName,
  onSave,
  onClose,
}: {
  open: boolean;
  existingNames: string[];
  suggestedName?: string;
  onSave: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setName(suggestedName ?? "");
      setError("");
    }
  }, [open, suggestedName]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Template name is required");
      return;
    }
    const isDupe = existingNames.some(
      (n) => n.toLowerCase() === trimmed.toLowerCase(),
    );
    if (isDupe) {
      setError("A saved deck with this name already exists");
      return;
    }
    onSave(trimmed);
    setName("");
    setError("");
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="Save deck list as template"
      panelClassName="w-[min(440px,92vw)] p-5"
    >
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-bold text-foreground">
            Save deck list as template
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs leading-snug text-muted-foreground">
          Saves the deck list, format, intent, and theme. The list will be
          re-matched against your inventory each time you load it.
        </p>
        <label className="text-xs font-semibold text-foreground">
          Template name
        </label>
        <input
          type="text"
          autoFocus
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
          }}
          placeholder="e.g. Charizard ex Casual"
          className={`w-full rounded-md border bg-muted px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 ${
            error
              ? "border-destructive ring-destructive/40"
              : "border-border focus:ring-primary/40"
          }`}
        />
        {error && (
          <p className="text-xs font-medium text-destructive">{error}</p>
        )}
        <div className="flex gap-2.5 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-md bg-muted px-3 py-2.5 text-sm font-semibold text-foreground hover:bg-muted/80"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex-[2] rounded-md bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            Save template
          </button>
        </div>
      </div>
    </Modal>
  );
}
